import { createContext, type Dispatch, type PropsWithChildren, type SetStateAction, useMemo, useState } from 'react'
import { useIsMobileVariant } from '../../../theme/hooks/use-is-mobile-variant'
import { type CardProps } from '../CardInternal'

export type CardState = {
  isDense?: CardProps['isDense']

  hasHeader: boolean

  /**
   * This only has influence on non mobile variants
   */
  hasDividerTop?: boolean

  /**
   * This only has influence on non mobile variants
   */
  hasDividerBottom?: boolean

  isMobile: boolean

  spacingToken: number
}

type CardActions = {
  setHasHeader: Dispatch<SetStateAction<boolean>>
}

export type CardContextProps = CardState & CardActions

export const defaultValue = {}

export const CardContext = createContext<CardContextProps>(defaultValue as CardContextProps)

type CardProviderProps = PropsWithChildren<Pick<CardState, 'isDense'> & { dividers?: CardProps['dividers'] }>

export function CardProvider({ children, isDense = false, dividers = ['top', 'bottom'] }: CardProviderProps) {
  const isMobile = useIsMobileVariant()
  const [hasHeader, setHasHeader] = useState(false)

  const value = useMemo(
    (): CardContextProps => ({
      isDense,
      hasHeader,
      setHasHeader,
      // we never expose the divider on mobile, never!
      hasDividerTop: isMobile ? false : (dividers?.some((d) => d === 'top') ?? false),
      hasDividerBottom: isMobile ? false : (dividers?.some((d) => d === 'bottom') ?? false),
      isMobile,
      spacingToken: isMobile ? 2 : 3,
    }),
    [isDense, hasHeader, dividers, isMobile]
  )

  return <CardContext.Provider value={value}>{children}</CardContext.Provider>
}
